import { Controller } from "@hotwired/stimulus"

// Chip/pill multi-select for client genres. Mirrors the release picker in the
// placement form (payment_form_controller): type to filter the shared vocabulary,
// pick a match or add a new genre, and each selection becomes a removable pill.
//
// Progressive enhancement: the server renders the already-selected genres as
// visible pills with submittable hidden inputs. This controller reads those on
// connect and takes over rendering. If it never connects (JS fails to load), the
// server-rendered selections remain visible and submit unchanged.
//
// Submission contract (unchanged from the checkbox build): every selected genre is
// a hidden `shared_client[genre_tags][]`; any selection not already in the
// vocabulary is ALSO emitted as `new_genres[]` so the server adds it to the shared
// list before validating.
export default class extends Controller {
  static values = { vocab: Array }
  static targets = ["pills", "input", "list", "newGenres"]

  connect() {
    // Seed from the server-rendered baseline pills (their data-genre attributes),
    // then take over rendering.
    this.selected = this.hasPillsTarget
      ? Array.from(this.pillsTarget.querySelectorAll("[data-genre]")).map((el) => el.dataset.genre)
      : []
    this.boundClose = this.close.bind(this)
    document.addEventListener("click", this.boundClose)
    this.render()
  }

  disconnect() {
    document.removeEventListener("click", this.boundClose)
  }

  vocabHas(name) {
    const n = name.toLowerCase()
    return this.vocabValue.some((v) => v.toLowerCase() === n)
  }

  // Canonical-cased value from the vocabulary when present, else the trimmed input.
  canonical(name) {
    const n = name.trim().toLowerCase()
    return this.vocabValue.find((v) => v.toLowerCase() === n) || name.trim()
  }

  isSelected(name) {
    const n = name.trim().toLowerCase()
    return this.selected.some((s) => s.toLowerCase() === n)
  }

  addValue(name) {
    const value = this.canonical(name)
    if (value && !this.isSelected(value)) this.selected.push(value)
    this.inputTarget.value = ""
    this.render()
    this.filter()
    this.inputTarget.focus()
  }

  remove(event) {
    event.stopPropagation()
    const g = event.currentTarget.dataset.genre.toLowerCase()
    this.selected = this.selected.filter((s) => s.toLowerCase() !== g)
    this.render()
    this.inputTarget.focus()
  }

  filter() {
    const q = this.inputTarget.value.trim().toLowerCase()
    const matches = this.vocabValue
      .filter((v) => !this.isSelected(v))
      .filter((v) => q.length === 0 || v.toLowerCase().includes(q))
      .slice(0, 50)

    let html = matches
      .map(
        (v) =>
          `<div class="genre-option" data-action="click->genre-picker#pick" data-genre="${this.esc(v)}">${this.esc(v)}</div>`
      )
      .join("")

    // Offer add-new when the typed text is not an exact vocabulary match and is not
    // already selected.
    const exact = this.vocabValue.some((v) => v.toLowerCase() === q)
    const raw = this.inputTarget.value.trim()
    if (raw.length > 0 && !exact && !this.isSelected(raw)) {
      html += `<div class="genre-option genre-option-new" data-action="click->genre-picker#pick" data-genre="${this.esc(raw)}">+ Add &ldquo;${this.esc(raw)}&rdquo; as a new genre</div>`
    }

    this.listTarget.innerHTML =
      html || `<div class="genre-option" style="color: var(--spt-text-secondary);">No matches</div>`
    this.listTarget.style.display = "block"
  }

  pick(event) {
    event.stopPropagation()
    this.addValue(event.currentTarget.dataset.genre)
  }

  // Enter commits the highlighted/first option, or adds the typed text as new.
  // Backspace on an empty input removes the last pill (Gmail-style). Space is NOT a
  // separator because genre names contain spaces (e.g. "Indie Rock").
  onKeydown(event) {
    if (event.key === "Enter") {
      event.preventDefault()
      const raw = this.inputTarget.value.trim()
      if (!raw) return
      const firstOpt = this.listTarget.querySelector(".genre-option[data-genre]")
      this.addValue(firstOpt ? firstOpt.dataset.genre : raw)
    } else if (event.key === "Backspace" && this.inputTarget.value === "" && this.selected.length > 0) {
      this.selected.pop()
      this.render()
    } else if (event.key === "Escape") {
      this.close()
    }
  }

  close(event) {
    if (event && this.element.contains(event.target)) return
    if (this.hasListTarget) this.listTarget.style.display = "none"
  }

  // Rebuild the pills (each with its submittable genre_tags[] hidden input and an x
  // to remove) and the new_genres[] hidden inputs for off-vocabulary picks.
  render() {
    this.pillsTarget.innerHTML = this.selected
      .map(
        (g) =>
          `<span class="genre-pill" data-genre="${this.esc(g)}">` +
          `<input type="hidden" name="shared_client[genre_tags][]" value="${this.esc(g)}">` +
          `<span class="genre-pill-label">${this.esc(g)}</span>` +
          `<button type="button" class="genre-pill-x" data-action="genre-picker#remove" data-genre="${this.esc(g)}" aria-label="Remove ${this.esc(g)}">&times;</button>` +
          `</span>`
      )
      .join("")

    this.newGenresTarget.innerHTML = this.selected
      .filter((g) => !this.vocabHas(g))
      .map((g) => `<input type="hidden" name="new_genres[]" value="${this.esc(g)}">`)
      .join("")
  }

  // Escapes for both text and attribute contexts (quotes included), since add-new
  // values are user-typed.
  esc(text) {
    if (text === null || text === undefined) return ""
    return String(text)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;")
  }
}
